<template>
  <div class="pt-[9px]">
    <b-form-group
      label="Title (Optional)"
      class="mb-3"
    >
      <b-input v-model="title" />
    </b-form-group>

    <b-form-group
      label="Code"
      class="mb-4"
    >
      <b-input
        v-model="hash"
        :disabled="loading"
        placeholder="Paste your block code"
        @input="onHashInput"
      />
    </b-form-group>

    <delete-confirm
      v-if="config.is_paid && config.code"
      title="Are you sure?"
      description="This will unlink the code from the hub."
      @confirm="unlinkCode"
    >
      <template #activator="{on}">
        <b-btn
          class="w-full mb-4"
          variant="outline-danger"
          v-on="on"
        >
          Unlink Code
        </b-btn>
      </template>
    </delete-confirm>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ModMixin from '@builder/components/mixins/ModMixin'
import DeleteConfirm from '@/components/editor/modals/DeleteConfirm.vue'

export default {
  name: 'EstageInternalEmbedMod',
  components: {DeleteConfirm},
  mixins: [ModMixin],

  data () {
    return {
      hash: '',
      loading: false
    }
  },

  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),

    title: {
      get () {
        return this.section?.options?.content?.code?.title
      },
      set (val) {
        this.$set(this.config, 'title', val)
      }
    },

    code () {
      return this.section?.options?.content?.code?.code
    }
  },

  methods: {
    sanitize (val) {
      return val
        .trim()
        .replace(/^\.+|\.+$/g, '')
        .replace(/\s+/g, '')
    },

    onHashInput (val) {
      const cleaned = this.sanitize(val)
      this.hash = cleaned

      // If empty, clear immediately without debounce
      if (!this.hash) {
        if (this._hashDebounce) clearTimeout(this._hashDebounce)
        this.updateCode()
        return
      }

      // Debounce updateCode to avoid firing on every keystroke
      if (this._hashDebounce) clearTimeout(this._hashDebounce)
      this._hashDebounce = setTimeout(() => {
        this.updateCode()
      }, 400) // adjust delay as needed
    },

    async updateCode () {
      this.loading = true
      try {
        if (!this.hash) {
          this.config.code = ''
          return false
        }
        const allowed = await this.$store.dispatch('favorites/checkPaidBlock', {hash: this.hash})
        this.config.is_paid = allowed.is_paid
        if (!allowed.status) {
          this.config.code = ''
          await this.$store.dispatch('favorites/showNotPaidToast')
          return
        }
        this.config.code = this.hash
      } catch (err) {
        this.config.code = ''
        this.$swal.fire({
          icon: 'error',
          toast: true,
          position: 'top-right',
          title: 'Something went wrong',
          text: 'Please try again in a moment.',
          showConfirmButton: false,
          timer: 5000
        })
      } finally {
        this.loading = false
      }
    },

    async unlinkCode () {
      await axios.delete(`api/projects/${this.project.id}/favorite-blocks/paid-block/${this.config.code}`)
        .then(() => {
          this.config.code = ''
          this.hash = ''
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'The code has been unlinked.',
            showConfirmButton: false,
            timer: 1500
          })
        })
        .catch(() => {
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: 'There was an error while unlinking the code.',
            showConfirmButton: false,
            timer: 1500
          })
        })
    }
  },
  beforeDestroy () {
    if (this._hashDebounce) {
      clearTimeout(this._hashDebounce)
      this._hashDebounce = null
    }
  },
  async created () {
    this.hash = this.section.options.content.code.code ?? ''
    if (this.config.is_paid === undefined && this.config.code) {
      const allowed = await this.$store.dispatch('favorites/checkPaidBlock', {hash: this.config.code})
      this.config.is_paid = allowed.is_paid
    }
  }
}
</script>